/**
 * Threat Scoring Utility
 * Calculates Threat-Adjusted Priority Scores (TAPS) by combining
 * maturity gap analysis with threat intelligence context
 */

import { getThreatProfileAdjustments } from './engagementDriver'

/**
 * Mapping of maturity categories to relevant threat actors and attack vectors
 * This connects security controls to the threats they mitigate
 */
const CATEGORY_THREAT_MAPPING = {
  'access-and-identity': {
    threatActors: ['credential_theft', 'insider_threat', 'opportunistic_hackers', 'fraud_operators'],
    attackVectors: ['credential_theft', 'phishing', 'account_takeover'],
    targetAssets: ['email_systems', 'customer_data', 'financial_systems'],
    description: 'Controls preventing unauthorized access and identity compromise'
  },
  'ai-adoption': {
    threatActors: ['nation_state', 'competitors', 'insider_threat'],
    attackVectors: ['api_exploitation', 'supply_chain', 'phishing'],
    targetAssets: ['research_data', 'ip_designs', 'business_apps'],
    description: 'Security of AI tool usage and implementation'
  },
  'ai-governance': {
    threatActors: ['nation_state', 'competitors', 'insider_threat', 'ransomware_gangs'],
    attackVectors: ['supply_chain', 'third_party_breach', 'api_exploitation'],
    targetAssets: ['customer_data', 'research_data', 'actuarial_data'],
    description: 'Oversight and control of AI systems and data usage'
  },
  'data-privacy': {
    threatActors: ['ransomware_gangs', 'insider_threat', 'nation_state', 'hacktivists'],
    attackVectors: ['ransomware', 'phishing', 'third_party_breach', 'credential_theft'],
    targetAssets: ['patient_data', 'customer_data', 'student_data', 'citizen_data', 'client_data'],
    description: 'Protection of sensitive and regulated data'
  },
  'governance-and-strategy': {
    threatActors: ['ransomware_gangs', 'nation_state', 'hacktivists'],
    attackVectors: ['ransomware', 'supply_chain', 'bec'],
    targetAssets: ['core_banking', 'policy_systems', 'production_systems'],
    description: 'Strategic security program governance and leadership'
  },
  'incident-readiness': {
    threatActors: ['ransomware_gangs', 'nation_state', 'opportunistic_hackers'],
    attackVectors: ['ransomware', 'phishing', 'unpatched_systems'],
    targetAssets: ['emergency_systems', 'core_banking', 'production_systems'],
    description: 'Ability to detect, respond to, and recover from incidents'
  },
  'operations-and-technology': {
    threatActors: ['ransomware_gangs', 'opportunistic_hackers', 'supply_chain_attackers'],
    attackVectors: ['ransomware', 'unpatched_systems', 'ot_exploitation', 'supply_chain'],
    targetAssets: ['production_systems', 'ot_networks', 'erp_systems', 'utility_systems'],
    description: 'Security of operational technology and business systems'
  },
  'people-and-culture': {
    threatActors: ['insider_threat', 'opportunistic_hackers', 'fraud_operators'],
    attackVectors: ['phishing', 'bec', 'credential_theft'],
    targetAssets: ['email_systems', 'customer_data', 'transaction_systems'],
    description: 'Security awareness, training, and human risk management'
  },
  'risk-and-incident-history': {
    threatActors: ['ransomware_gangs', 'nation_state', 'cybercrime_syndicates'],
    attackVectors: ['ransomware', 'phishing', 'bec', 'third_party_breach'],
    targetAssets: ['customer_data', 'financial_systems', 'core_banking'],
    description: 'Risk assessment practices and incident learning'
  },
  'security-and-it-program-structure': {
    threatActors: ['ransomware_gangs', 'nation_state', 'supply_chain_attackers'],
    attackVectors: ['supply_chain', 'third_party_breach', 'unpatched_systems'],
    targetAssets: ['core_banking', 'policy_systems', 'erp_systems'],
    description: 'Security program organization and resource allocation'
  },
  'technology-environment': {
    threatActors: ['ransomware_gangs', 'opportunistic_hackers', 'nation_state'],
    attackVectors: ['ransomware', 'unpatched_systems', 'cloud_compromise', 'ot_exploitation'],
    targetAssets: ['production_systems', 'ehr_systems', 'technology_systems', 'building_systems'],
    description: 'Security of infrastructure and technology stack'
  },
  'technology-and-systems': {
    threatActors: ['ransomware_gangs', 'opportunistic_hackers', 'supply_chain_attackers'],
    attackVectors: ['ransomware', 'unpatched_systems', 'supply_chain', 'api_exploitation'],
    targetAssets: ['core_banking', 'policy_systems', 'learning_systems', 'citizen_services'],
    description: 'Security of business-critical systems and applications'
  },
  'data-management': {
    threatActors: ['ransomware_gangs', 'insider_threat', 'nation_state'],
    attackVectors: ['ransomware', 'credential_theft', 'cloud_compromise'],
    targetAssets: ['customer_data', 'patient_data', 'document_management', 'research_data'],
    description: 'Data classification, handling, and protection practices'
  },
  'program-and-risk-reporting': {
    threatActors: ['insider_threat', 'nation_state', 'hacktivists'],
    attackVectors: ['bec', 'phishing', 'third_party_breach'],
    targetAssets: ['financial_systems', 'email_systems', 'billing_systems'],
    description: 'Security metrics, reporting, and board communication'
  }
}

/**
 * Get the category-to-threat mapping configuration
 * @returns {Object} Category threat mappings
 */
export function getCategoryThreatMapping() {
  return CATEGORY_THREAT_MAPPING
}

/**
 * Calculate threat relevance score for a maturity category
 * based on the threat profile and user-adjusted weights
 *
 * @param {string} categoryId - Maturity category ID
 * @param {Object} threatContext - User's threat context customizations
 * @param {Object} profile - Selected threat profile from threatProfiles.json
 * @returns {Object} Threat relevance details
 */
export function calculateThreatRelevance(categoryId, threatContext, profile) {
  const mapping = CATEGORY_THREAT_MAPPING[categoryId]
  if (!mapping || !profile) {
    return { score: 1.0, factors: [], rawScore: 1.0 }
  }

  const factors = []
  let totalWeight = 0
  let weightedSum = 0

  // Calculate threat actor relevance
  const actorWeights = threatContext?.threat_actor_weights || {}
  profile.threatActors?.forEach(actor => {
    if (mapping.threatActors.includes(actor.id)) {
      const userWeight = actorWeights[actor.id] ?? actor.defaultWeight
      const relevance = actor.likelihood * userWeight
      factors.push({
        type: 'actor',
        id: actor.id,
        name: actor.name,
        likelihood: actor.likelihood,
        weight: userWeight,
        relevance
      })
      weightedSum += relevance
      totalWeight += 1
    }
  })

  // Calculate attack vector relevance
  const vectorWeights = threatContext?.attack_vector_weights || {}
  profile.attackVectors?.forEach(vector => {
    if (mapping.attackVectors.includes(vector.id)) {
      const userWeight = vectorWeights[vector.id] ?? vector.defaultWeight
      const relevance = vector.prevalence * userWeight
      factors.push({
        type: 'vector',
        id: vector.id,
        name: vector.name,
        prevalence: vector.prevalence,
        weight: userWeight,
        relevance
      })
      weightedSum += relevance
      totalWeight += 1
    }
  })

  // Calculate target asset relevance
  const assetWeights = threatContext?.target_asset_weights || {}
  profile.targetAssets?.forEach(asset => {
    if (mapping.targetAssets.includes(asset.id)) {
      const userWeight = assetWeights[asset.id] ?? asset.defaultWeight
      const relevance = asset.criticality * userWeight
      factors.push({
        type: 'asset',
        id: asset.id,
        name: asset.name,
        criticality: asset.criticality,
        weight: userWeight,
        relevance
      })
      weightedSum += relevance
      totalWeight += 1
    }
  })

  // Calculate normalized threat relevance score (0-1 range, typically 0.3-1.0)
  const rawScore = totalWeight > 0 ? weightedSum / totalWeight : 0.5

  // Scale to 0.5-1.5 range for use as multiplier
  const score = 0.5 + rawScore

  return {
    score: Math.round(score * 100) / 100,
    rawScore: Math.round(rawScore * 100) / 100,
    factors,
    categoryDescription: mapping.description
  }
}

/**
 * Calculate Threat-Adjusted Priority Score (TAPS) for all categories
 * TAPS = Gap Severity × Threat Relevance × Impact Weight × Engagement Driver Boost
 *
 * @param {Object} gapAnalysis - Enhanced gap analysis from scoring.js
 * @param {Object} threatContext - User's threat context from assessment
 * @param {Object} impactData - User's impact calibration data
 * @param {Object} profile - Selected threat profile
 * @param {Object} engagementContext - Engagement driver context (optional)
 * @returns {Object} TAPS results with prioritized categories
 */
export function calculateTAPS(gapAnalysis, threatContext, impactData, profile, engagementContext = null) {
  if (!gapAnalysis?.impacts) {
    return { categories: [], summary: null }
  }

  // Get engagement driver adjustments if available
  const engagementAdjustments = engagementContext
    ? getThreatProfileAdjustments(engagementContext)
    : { domainBoosts: {}, threatCategoryBoosts: {}, relevanceMultiplier: 1.0 }

  const categories = []

  gapAnalysis.impacts.forEach(impact => {
    // Map domain to category ID (domain names need to match category IDs)
    const categoryId = domainToCategoryId(impact.domain)
    if (!categoryId) return

    // Get threat relevance for this category
    const threatRelevance = calculateThreatRelevance(categoryId, threatContext, profile)

    // Calculate gap severity multiplier (0-2 scale based on gap)
    const gapSeverityMultiplier = calculateGapSeverityMultiplier(impact.gap)

    // Calculate impact weight based on business impact data
    const impactWeight = calculateImpactWeight(impact, impactData)

    // Apply engagement driver domain boost
    const engagementDomainBoost = engagementAdjustments.domainBoosts[categoryId] || 1.0

    // Calculate TAPS score with engagement driver boost
    const baseTapsScore = gapSeverityMultiplier * threatRelevance.score * impactWeight
    const tapsScore = baseTapsScore * engagementDomainBoost

    categories.push({
      domain: impact.domain,
      categoryId,
      gapScore: impact.gap,
      gapSeverity: impact.severity,
      gapSeverityMultiplier: Math.round(gapSeverityMultiplier * 100) / 100,
      threatRelevance: threatRelevance.score,
      threatFactors: threatRelevance.factors,
      impactWeight: Math.round(impactWeight * 100) / 100,
      engagementDomainBoost: engagementDomainBoost > 1.0 ? Math.round(engagementDomainBoost * 100) / 100 : null,
      baseTapsScore: Math.round(baseTapsScore * 100) / 100,
      tapsScore: Math.round(tapsScore * 100) / 100,
      businessImpact: impact.businessImpact,
      costRange: impact.costRange,
      timeframe: impact.timeframe,
      recommendedActions: impact.recommendedActions
    })
  })

  // Sort by TAPS score (highest priority first)
  categories.sort((a, b) => b.tapsScore - a.tapsScore)

  // Assign ranks
  categories.forEach((cat, index) => {
    cat.rank = index + 1
    cat.priorityLevel = getPriorityLevel(cat.tapsScore, index)
  })

  // Calculate summary statistics
  const engagementBoostsApplied = categories.some(c => c.engagementDomainBoost)

  const summary = {
    totalCategories: categories.length,
    criticalPriority: categories.filter(c => c.priorityLevel === 'Critical').length,
    highPriority: categories.filter(c => c.priorityLevel === 'High').length,
    mediumPriority: categories.filter(c => c.priorityLevel === 'Medium').length,
    lowPriority: categories.filter(c => c.priorityLevel === 'Low').length,
    averageTAPS: categories.length > 0
      ? Math.round((categories.reduce((sum, c) => sum + c.tapsScore, 0) / categories.length) * 100) / 100
      : 0,
    maxTAPS: categories.length > 0 ? categories[0].tapsScore : 0,
    engagementBoostsApplied,
    engagementRelevanceMultiplier: engagementAdjustments.relevanceMultiplier
  }

  return { categories, summary }
}

/**
 * Map domain name to category ID for threat mapping lookup
 */
function domainToCategoryId(domain) {
  const mapping = {
    'Financial': 'governance-and-strategy',
    'Reputation & Trust': 'incident-readiness',
    'Regulatory & Compliance': 'program-and-risk-reporting',
    'Data Protection & Privacy': 'data-privacy',
    'Security & Technology': 'security-and-it-program-structure',
    'Operational Resilience': 'operations-and-technology',
    'AI & Emerging Technology': 'ai-adoption',
    'Third-Party & Supply Chain': 'technology-and-systems'
  }
  return mapping[domain] || null
}

/**
 * Calculate gap severity multiplier (0-2 scale)
 * Larger negative gaps = higher multiplier
 */
function calculateGapSeverityMultiplier(gap) {
  if (gap >= 0) return 0.2      // Aligned - minimal priority
  if (gap >= -10) return 0.5    // Minor gap
  if (gap >= -20) return 1.0    // Moderate gap
  if (gap >= -35) return 1.5    // Significant gap
  return 2.0                     // Critical gap
}

/**
 * Calculate impact weight based on business context
 */
function calculateImpactWeight(impact, impactData) {
  let weight = 1.0

  // Adjust based on impact categories
  if (impact.impactCategories) {
    const highImpacts = Object.values(impact.impactCategories).filter(v => v === 'High').length
    weight += highImpacts * 0.15
  }

  // Adjust based on organization revenue (higher revenue = higher stakes)
  if (impactData?.estimated_annual_revenue) {
    const revenue = impactData.estimated_annual_revenue
    if (revenue > 100000000) weight += 0.3      // >$100M
    else if (revenue > 25000000) weight += 0.2  // >$25M
    else if (revenue > 5000000) weight += 0.1   // >$5M
  }

  // Adjust if no insurance coverage
  if (!impactData?.has_cyber_insurance) {
    weight += 0.1
  }

  return Math.min(weight, 2.0) // Cap at 2.0
}

/**
 * Determine priority level based on TAPS score and rank
 */
function getPriorityLevel(tapsScore, rank) {
  if (tapsScore >= 2.0 || rank < 2) return 'Critical'
  if (tapsScore >= 1.5 || rank < 4) return 'High'
  if (tapsScore >= 0.8 || rank < 7) return 'Medium'
  return 'Low'
}

/**
 * Get prioritized list of categories sorted by TAPS
 * @param {Array} categories - TAPS categories from calculateTAPS
 * @returns {Array} Sorted categories
 */
export function prioritizeByTAPS(categories) {
  return [...categories].sort((a, b) => b.tapsScore - a.tapsScore)
}

/**
 * Get top N priority items for executive summary
 */
export function getTopPriorities(tapsResults, count = 5) {
  if (!tapsResults?.categories) return []
  return tapsResults.categories.slice(0, count)
}

/**
 * Calculate threat context summary for display
 */
export function getThreatContextSummary(profile, threatContext) {
  if (!profile) return null

  const actorWeights = threatContext?.threat_actor_weights || threatContext?.actorWeights || {}
  const vectorWeights = threatContext?.attack_vector_weights || threatContext?.vectorWeights || {}
  const assetWeights = threatContext?.target_asset_weights || threatContext?.assetWeights || {}

  // Get top threat actors by weighted likelihood
  const topActors = (profile.threatActors || [])
    .map(actor => ({
      ...actor,
      effectiveWeight: actorWeights[actor.id] ?? actor.defaultWeight ?? 1,
      adjustedLikelihood: actor.likelihood * (actorWeights[actor.id] ?? actor.defaultWeight ?? 1)
    }))
    .sort((a, b) => b.adjustedLikelihood - a.adjustedLikelihood)
    .slice(0, 4)

  // Get top attack vectors by weighted prevalence
  const topVectors = (profile.attackVectors || [])
    .map(vector => ({
      ...vector,
      effectiveWeight: vectorWeights[vector.id] ?? vector.defaultWeight ?? 1,
      adjustedPrevalence: vector.prevalence * (vectorWeights[vector.id] ?? vector.defaultWeight ?? 1)
    }))
    .sort((a, b) => b.adjustedPrevalence - a.adjustedPrevalence)
    .slice(0, 4)

  // Get top target assets by weighted criticality
  const topAssets = (profile.targetAssets || [])
    .map(asset => ({
      ...asset,
      effectiveWeight: assetWeights[asset.id] ?? asset.defaultWeight,
      adjustedCriticality: (asset.criticality || 0) * (assetWeights[asset.id] ?? asset.defaultWeight ?? 1)
    }))
    .sort((a, b) => b.adjustedCriticality - a.adjustedCriticality)
    .slice(0, 4)

  return {
    profile: {
      key: profile.verticalKey,
      name: profile.verticalName,
      description: profile.description
    },
    topThreatActors: topActors,
    topAttackVectors: topVectors,
    topTargetAssets: topAssets,
    regulatoryContext: profile.regulatoryContext,
    hasCustomizations: Object.keys(actorWeights).length > 0 || Object.keys(vectorWeights).length > 0 || Object.keys(assetWeights).length > 0
  }
}
